import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { api } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Package, ShoppingBag, Mail, MessageSquare, LogOut } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, adminToken, logout } = useAdmin();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalProducts: 0,
    totalSubscribers: 0,
    totalMessages: 0
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }

    const fetchStats = async () => {
      try {
        const [orders, products, subscribers, messages] = await Promise.all([
          api.adminGetOrders(adminToken),
          api.getProducts(),
          api.adminGetNewsletterSubscribers(adminToken),
          api.adminGetContactMessages(adminToken)
        ]);

        setStats({
          totalOrders: orders.length,
          totalProducts: products.length,
          totalSubscribers: subscribers.length,
          totalMessages: messages.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, [isAdmin, adminToken, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline">View Store</Button>
              </Link>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h2>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card 
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/orders')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card 
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/products')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Products</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card 
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/newsletter')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Newsletter Subscribers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalSubscribers}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card 
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/admin/messages')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Contact Messages</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalMessages}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <MessageSquare className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/admin/products">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Manage Products</h3>
              <p className="text-gray-600">Add, edit, or remove products from your store</p>
            </Card>
          </Link>

          <Link to="/admin/collections">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Manage Collections</h3>
              <p className="text-gray-600">Organize products into collections</p>
            </Card>
          </Link>

          <Link to="/admin/page-editor">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <h3 className="text-lg font-medium text-purple-900 mb-2">🎨 Visual Page Editor</h3>
              <p className="text-purple-700">Edit landing page content, images & videos</p>
            </Card>
          </Link>

          <Link to="/admin/qr-templates">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
              <h3 className="text-lg font-medium text-blue-900 mb-2">📱 QR Template Manager</h3>
              <p className="text-blue-700">Create and manage QR code templates</p>
            </Card>
          </Link>

          <Link to="/admin/testing">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
              <h3 className="text-lg font-medium text-green-900 mb-2">🧪 Testing Dashboard</h3>
              <p className="text-green-700">Monitor API and feature test results</p>
            </Card>
          </Link>

          <Link to="/admin/photo-booth">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200">
              <h3 className="text-lg font-medium text-orange-900 mb-2">📸 Photo Booth Templates</h3>
              <p className="text-orange-700">AI background removal & circle layouts</p>
            </Card>
          </Link>

          <Link to="/admin/photobooth-page">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <h3 className="text-lg font-medium text-purple-900 mb-2">🎬 Live Photo Booth Page</h3>
              <p className="text-purple-700">Edit page content, videos & images</p>
            </Card>
          </Link>

          <Link to="/admin/clients">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-pink-50 to-purple-50 border-2 border-pink-200">
              <h3 className="text-lg font-medium text-pink-900 mb-2">👥 Client Submissions</h3>
              <p className="text-pink-700">View all client names & contacts</p>
            </Card>
          </Link>

          <Link to="/admin/shipping">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
              <h3 className="text-lg font-medium text-green-900 mb-2">🚚 Shipping Settings</h3>
              <p className="text-green-700">Configure shipping options & free shipping</p>
            </Card>
          </Link>

          <Link to="/admin/orders">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">View Orders</h3>
              <p className="text-gray-600">Manage customer orders and update status</p>
            </Card>
          </Link>

          <Link to="/admin/newsletter">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Newsletter Subscribers</h3>
              <p className="text-gray-600">View and manage email subscribers</p>
            </Card>
          </Link>

          <Link to="/admin/contacts">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Contact Messages</h3>
              <p className="text-gray-600">View messages from customers</p>
            </Card>
          </Link>

          <Link to="/admin/coupons">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Discount Coupons</h3>
              <p className="text-gray-600">Create and manage discount codes</p>
            </Card>
          </Link>

          <Link to="/admin/settings">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Settings</h3>
              <p className="text-gray-600">Configure shipping and other settings</p>
            </Card>
          </Link>

          <Link to="/admin/affiliates">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Affiliate Program</h3>
              <p className="text-gray-600">Manage affiliates and track commissions</p>
            </Card>
          </Link>

          <Link to="/admin/seed-products">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200">
              <h3 className="text-lg font-medium text-amber-900 mb-2">🌱 Seed Products</h3>
              <p className="text-amber-700">Add default products (Oreo, Cards, etc.)</p>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
